import { useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import renderSections from 'components/sections/render';
import useSWR from 'swr';
import useProductViewItemTracking from 'hooks/tracking/useProductViewItemTracking';
import RelatedProducts from 'components/sections/relatedProducts/relatedProducts';
import { useSettings, useFreeItemLabel } from 'hooks';
import { useGlobalModules } from 'contexts';
import Expander from 'components/shared/expander/expander';
import Reviews from 'components/shared/reviewsIO/reviews/reviews';
import styles from './product.module.scss';
import ProductInfo from './productInfo';
import ProductSpecifications from './productSpecifications';
import ProductGallery from './productGallery/productGallery';
import Breadcrumbs from '../../layout/breadcrumbs';

const PageProduct = ({ data, locale }) => {
  const { thumbnailImage, shopifyData: cachedShopifyData, additionalSections, productMedia } = data;

  const [shopifyData, setShopifyData] = useState({
    productSKU: cachedShopifyData?.variants[0].sku,
    productPrice: cachedShopifyData?.variants[0].price,
    currencyCode: cachedShopifyData?.variants[0].currencyCode,
    productCompareAtPrice: cachedShopifyData?.variants[0].compareAtPrice,
    productId: cachedShopifyData?.variants[0].product_id,
    outOfStock: cachedShopifyData?.tags?.includes('out-of-stock'),
    variantId: cachedShopifyData?.variants[0].id,
    isFreeItem: cachedShopifyData?.tags?.includes('free-item'),
    collectionIds: [],
  });

  const { trackedItemsSelectList } = useSettings();
  const globalModules = useGlobalModules();
  const freeItemLabel = useFreeItemLabel(cachedShopifyData?.variants?.[0]?.product_id, locale);

  const fetcher = async endpoint => {
    const responseRaw = await fetch(endpoint);
    const response = await responseRaw.json();
    return response;
  };

  const swrOptions = {
    refreshInterval: 60000,
  };

  const { data: updatedProductData } = useSWR(
    cachedShopifyData?.variants?.[0]?.product_id
      ? `/api/shopify/utils/getProduct?productId=${cachedShopifyData?.variants?.[0]?.product_id}&locale=${locale}`
      : null,
    fetcher,
    swrOptions
  );

  const { ingredientsTitle, disclaimerText, directionOfUseTitle } =
    globalModules.translations.product;

  const hasRelatedProductsSection = additionalSections.some(
    section => section.type === 'SectionRelatedProducts'
  );
  const hasReviewsSection = additionalSections.some(
    section => section.type === 'SectionProductReviews'
  );

  // Build the ordered sections list.
  // If SectionProductReviews is not explicitly added, inject it before Related Products as default.
  // If SectionRelatedProducts is not explicitly added, inject it at the end as fallback.
  const orderedSections = [...additionalSections];
  if (!hasReviewsSection) {
    orderedSections.push({ type: 'SectionProductReviews' });
  }
  if (!hasRelatedProductsSection) {
    orderedSections.push({ type: 'SectionRelatedProducts' });
  }

  useEffect(() => {
    if (typeof window === 'undefined' || typeof updatedProductData !== 'object') return;
    if (updatedProductData.status === 200) {
      setShopifyData(updatedProductData.data);

      const productSelectListObj = trackedItemsSelectList?.find(
        p => p?.productId === updatedProductData.data.productId
      );

      const { items: productSpecs } = data?.productSpecsCollection;
      const productType = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Producttype' ||
          type.filterType === 'Type de produit' ||
          type.filterType === 'Product type'
      );
      const productCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Productcategorie' ||
          type.filterType === 'Catégorie de produit' ||
          type.filterType === 'Product Category'
      );
      const productSubCategory = productSpecs?.find(
        ({ type }) =>
          type.filterType === 'Subcategorie' ||
          type.filterType === 'Sous-catégorie' ||
          type.filterType === 'Sub Category'
      );

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductViewItemTracking({
        product: {
          name: updatedProductData.data.productTitle,
          id: updatedProductData.data.productId,
          category: productType?.value,
          category2: productCategory?.value,
          category3: productSubCategory?.value,
          price: updatedProductData.data.productPrice,
          outOfStock: Number(!updatedProductData.data?.outOfStock), // negation outOfStock to convert to in_stock for tracking
          itemListName: productSelectListObj?.itemListName,
          itemIndex: productSelectListObj?.itemIndex,
          currencyCode: updatedProductData.data?.currencyCode,
          imageUrl: thumbnailImage?.url,
          compareAtPrice: updatedProductData?.data?.productCompareAtPrice,
        },
      });
    } else {
      console.error(updatedProductData.error);
    }
  }, [updatedProductData]);

  return (
    <>
      <section>
        <Breadcrumbs locale={locale} data={data} />
        <div className={styles.productPage}>
          <ProductGallery
            key={data.slug}
            thumbnail={thumbnailImage}
            productMedia={productMedia}
            productData={{
              ...data,
              shopifyData,
            }}
            freeItemLabel={freeItemLabel}
          />
          <div className={styles.productInfo}>
            <ProductInfo
              locale={locale}
              productData={{
                ...data,
                shopifyData,
              }}
            />
            <div className={styles.topDivider} />
            <div className={styles.productExpanders}>
              {data.showSpecifications && (
                <ProductSpecifications
                  specs={data.productSpecsCollection?.items}
                  size={data.size}
                  ownOrder={data.ownSpecificationOrder}
                />
              )}

              {data.directionOfUse && (
                <Expander title={directionOfUseTitle}>
                  <RichText text={data.directionOfUse.json} />
                </Expander>
              )}

              {data.expandersCollection.items.map(expander => (
                <Expander title={expander.title}>
                  <RichText text={expander?.content?.json} />
                </Expander>
              ))}

              {data.ingredients && (
                <Expander title={ingredientsTitle}>
                  <RichText text={data.ingredients.json} />
                </Expander>
              )}
            </div>
            <RichText className={styles.disclaimerText} text={disclaimerText.json} />

            <div className={styles.variantTitleAndEAN}>
              <p className={styles.singleVariant}>{data.size}</p>
              <p className={styles.eanNumber}>{data.eanNumber}</p>
            </div>
            <div className={styles.bottomDivider} />
          </div>
        </div>
        <div className={styles.additionalSections}>
          {orderedSections.map((section, i) => {
            if (section.type === 'SectionProductReviews') {
              return (
                <Reviews
                  key={`reviews-${i}`}
                  SKU={cachedShopifyData?.variants?.[0]?.sku}
                  locale={locale}
                  className={styles.reviews}
                />
              );
            }
            if (section.type === 'SectionRelatedProducts') {
              return (
                <RelatedProducts
                  key={`related-${i}`}
                  productId={cachedShopifyData?.variants?.[0]?.product_id}
                  data={section.relatedProducts ? section : undefined}
                  locale={locale}
                  className={styles.relatedProductsSection}
                />
              );
            }
            return renderSections('', locale, [section], undefined);
          })}
        </div>
      </section>
    </>
  );
};

export default PageProduct;
